import React, { memo, useMemo, type ReactNode } from 'react';
import { Box, Card, CardContent, Tooltip, Typography, alpha } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';
import { metricSummaryTooltipProps } from '../../constants/metricTooltips';
import {
  type ColorScheme,
  formatCompactUsd,
  getColorScheme,
  METRIC_CARD_APPLE_RADIUS,
  METRIC_CARD_TITLE_FONT_SIZE,
  METRIC_CARD_TITLE_LINE_HEIGHT,
  METRIC_CARD_TITLE_MARGIN_BOTTOM,
  metricCardAppleShellSx,
  metricCardEntranceSx,
} from './metricCardTheme';

export type { ColorScheme } from './metricCardTheme';
export { getColorScheme, formatCompactUsd } from './metricCardTheme';

type TrendDirection = 'up' | 'down';

interface MetricIconBadgeProps {
  /** MUI icon component */
  icon: React.ElementType;
  scheme: ColorScheme;
  /** Class name targeted by parent Card `&:hover` rules */
  iconClassName: string;
  boxPx: number;
  iconFontPx: number;
  borderRadiusPx?: number;
  /** MUI spacing for `mr` (theme units). */
  marginRight?: number;
}

/**
 * Rounded icon container used across mini, status, and revenue metric cards.
 */
const MetricIconBadge = memo(function MetricIconBadge({
  icon: Icon,
  scheme,
  iconClassName,
  boxPx,
  iconFontPx,
  borderRadiusPx = 8,
  marginRight = 2,
}: MetricIconBadgeProps) {
  return (
    <Box
      className={iconClassName}
      sx={{
        width: boxPx,
        height: boxPx,
        borderRadius: `${borderRadiusPx}px`,
        background: `linear-gradient(135deg, ${alpha(scheme.text, 0.2)} 0%, ${alpha(scheme.text, 0.1)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: scheme.text,
        mr: marginRight,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        flexShrink: 0,
        border: `1px solid ${alpha(scheme.text, 0.15)}`,
      }}
    >
      <Icon sx={{ fontSize: iconFontPx }} />
    </Box>
  );
});

interface MetricTrendProps {
  trend: TrendDirection;
  trendValue: number;
  scheme: ColorScheme;
  /** e.g. ` from last period` for revenue card */
  suffix?: string;
  fontSize?: string;
  /** When true, adds top margin (mini card layout). */
  withTopSpacing?: boolean;
}

/**
 * Small up/down arrow with percentage (shared by all metric variants).
 */
const MetricTrend = memo(function MetricTrend({
  trend,
  trendValue,
  scheme,
  suffix = '',
  fontSize = '0.7rem',
  withTopSpacing = false,
}: MetricTrendProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ...(withTopSpacing ? { mt: 0.5 } : {}) }}>
      <Box
        component="span"
        sx={{
          color: alpha(scheme.text, 0.8),
          fontSize,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {trend === 'up' ? '↗' : '↘'} {trendValue}%
        {suffix}
      </Box>
    </Box>
  );
});

function useMetricScheme(color: string | undefined): ColorScheme {
  return useMemo(() => getColorScheme(color ?? SEARCE_COLORS.primary), [color]);
}

export interface MiniMetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  trend?: TrendDirection | null;
  trendValue?: number;
  tooltip?: ReactNode;
}

/**
 * Compact metric tile with icon, title, value, and optional trend.
 */
export const MiniMetricCard = memo(function MiniMetricCard({
  title,
  value,
  icon,
  color = SEARCE_COLORS.primary,
  trend = null,
  trendValue = 0,
  tooltip,
}: MiniMetricCardProps) {
  const scheme = useMetricScheme(color);

  const card = (
    <Card
      className="mini-metric-card"
      sx={[
        {
          height: 85,
          background: scheme.bg,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
        },
        metricCardAppleShellSx(scheme.hover, {
          '& .metric-icon': { transform: 'scale(1.1)' },
        }),
        metricCardEntranceSx,
      ]}
    >
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <MetricIconBadge icon={icon} scheme={scheme} iconClassName="metric-icon" boxPx={32} iconFontPx={16} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: alpha(scheme.text, 0.9),
              fontSize: METRIC_CARD_TITLE_FONT_SIZE,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: METRIC_CARD_TITLE_LINE_HEIGHT,
              mb: METRIC_CARD_TITLE_MARGIN_BOTTOM,
            }}
          >
            {title}
          </Typography>

          <Typography
            className="metric-value"
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: '1.5rem',
              color: scheme.text,
              lineHeight: 1.1,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>

          {trend ? (
            <MetricTrend trend={trend} trendValue={trendValue} scheme={scheme} withTopSpacing />
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} {...metricSummaryTooltipProps}>
        <Box sx={{ width: '100%', display: 'block', borderRadius: METRIC_CARD_APPLE_RADIUS }}>{card}</Box>
      </Tooltip>
    );
  }

  return card;
});

export interface StatusCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  total?: number;
  trend?: TrendDirection | null;
  trendValue?: number;
}

/**
 * Metric card with horizontal progress vs `total` and optional trend in the footer.
 */
export const StatusCard = memo(function StatusCard({
  title,
  value,
  icon,
  color = SEARCE_COLORS.primary,
  total = 0,
  trend = null,
  trendValue = 0,
}: StatusCardProps) {
  const scheme = useMetricScheme(color);
  const percentage = total > 0 ? (Number(value) / total) * 100 : 0;

  return (
    <Card
      className="status-card"
      sx={[
        {
          height: 105,
          background: scheme.bg,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
        },
        metricCardAppleShellSx(scheme.hover, {
          '& .status-icon': { transform: 'scale(1.1) rotate(5deg)' },
          '& .progress-bar': { width: '100%' },
        }),
        metricCardEntranceSx,
      ]}
    >
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <MetricIconBadge
            icon={icon}
            scheme={scheme}
            iconClassName="status-icon"
            boxPx={32}
            iconFontPx={16}
            marginRight={1.5}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(scheme.text, 0.9),
                fontSize: METRIC_CARD_TITLE_FONT_SIZE,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: METRIC_CARD_TITLE_LINE_HEIGHT,
                mb: METRIC_CARD_TITLE_MARGIN_BOTTOM,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: scheme.text,
                lineHeight: 1.1,
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', mb: 1 }}>
          <Box
            className="progress-bar"
            sx={{
              height: 4,
              borderRadius: 2,
              background: alpha(scheme.text, 0.1),
              overflow: 'hidden',
              transition: 'width 0.6s ease-out',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${percentage}%`,
                background: `linear-gradient(90deg, ${scheme.text} 0%, ${alpha(scheme.text, 0.8)} 100%)`,
                borderRadius: 2,
                transition: 'width 0.6s ease-out',
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: alpha(scheme.text, 0.8),
              fontSize: '0.65rem',
              fontWeight: 500,
            }}
          >
            {percentage.toFixed(1)}% of total
          </Typography>

          {trend ? (
            <MetricTrend trend={trend} trendValue={trendValue} scheme={scheme} fontSize="0.65rem" />
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
});

export interface ModernRevenueCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color?: string;
  showProgress?: boolean;
  progressValue?: number;
  trend?: TrendDirection | null;
  trendValue?: number;
}

/**
 * Larger revenue-style card with optional conic progress ring and compact currency value.
 */
export const ModernRevenueCard = memo(function ModernRevenueCard({
  title,
  value,
  subtitle,
  icon,
  color = SEARCE_COLORS.primary,
  showProgress = false,
  progressValue = 0,
  trend = null,
  trendValue = 0,
}: ModernRevenueCardProps) {
  const scheme = useMetricScheme(color);

  const displayValue = typeof value === 'number' ? formatCompactUsd(value) : value;

  return (
    <Card
      sx={[
        {
          height: 140,
          background: scheme.bg,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
        },
        metricCardAppleShellSx(scheme.hover, {
          '& .revenue-icon': { transform: 'scale(1.1)' },
        }),
        metricCardEntranceSx,
      ]}
    >
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <MetricIconBadge
            icon={icon}
            scheme={scheme}
            iconClassName="revenue-icon"
            boxPx={36}
            iconFontPx={18}
            borderRadiusPx={10}
            marginRight={1.5}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(scheme.text, 0.9),
                fontSize: METRIC_CARD_TITLE_FONT_SIZE,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: METRIC_CARD_TITLE_LINE_HEIGHT,
                mb: METRIC_CARD_TITLE_MARGIN_BOTTOM,
              }}
            >
              {title}
            </Typography>

            {subtitle ? (
              <Typography
                variant="caption"
                sx={{
                  color: alpha(scheme.text, 0.7),
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              className="revenue-value"
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: '1.4rem',
                color: scheme.text,
                lineHeight: 1.1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 0.5,
              }}
            >
              {displayValue}
            </Typography>

            {trend ? (
              <MetricTrend
                trend={trend}
                trendValue={trendValue}
                scheme={scheme}
                suffix=" from last period"
                fontSize="0.65rem"
              />
            ) : null}
          </Box>

          {showProgress ? (
            <Box sx={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `conic-gradient(${scheme.text} ${progressValue * 3.6}deg, ${alpha(scheme.text, 0.1)} 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    width: '70%',
                    height: '70%',
                    borderRadius: '50%',
                    background: scheme.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: scheme.text,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                    }}
                  >
                    {progressValue.toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
});
